import type { ForkId } from './skills.ts';
import { FORKS } from './skills.ts';
import { HEROES, SKILL_FX, WARRIOR, type SkillId } from './heroes.ts';
import { UNIQUE_FORMS } from './forms.ts';
import type { UniqueId } from './saga.ts';
import type { Lay, Num, SkillCast, SkillField, Step } from './skill-cast.ts';

/** What a variant does to its skill's plain numbers (the rest of what it does is in the skill's program). */
export type FieldPatch = { bp: number } | { add: number } | { set: number };

/** One number with a variant, another without. */
const byFork = (m: Partial<Record<ForkId, Num>>, e: Num): Num => ({ fork: m, else: e });
/** A field (or number) times a variant's bp, untouched without it. */
const fbp = (v: Num, fork: ForkId, bp: number): Num => ({ mul: [v, byFork({ [fork]: bp }, 10000)] });
/** A skill's damage on this hero. */
const pow = (v: Num): Num => ({ pow: v });
const DMG: Num = pow('damage');
const only = (fork: ForkId) => ({ fork });
const when = (fork: ForkId, v: Lay): Lay => ({ when: only(fork), v });
const after = (v: Num): Lay => ({ after: v });
const rec = (r: Record<string, Num | { pid: true }>): Lay => ({ rec: r });
const recordNear = (at: 'h' | 'p', r: Num, targetable = true): Step[] => [
  { k: 'near', at, r, ...(targetable ? { pick: ['t'] as const } : {}), into: 'n' },
  { k: 'record', of: 'n' },
];
const recordAllies = (at: 'h' | 'p', r: Num): Step[] => [{ k: 'allies', at, r, into: 'n' }, { k: 'record', of: 'n' }];
/** The form bits of a unique piece on what its skill leaves (absent without a form: plays and hashes as before). */
const formMark = (id: UniqueId): Lay => ({ when: { anyForm: id }, v: { formBits: id } });
/** Maelis's kit's pair: each grenade at this share. */
const KIT_PAIR: Num = UNIQUE_FORMS.maelisKit.b.fx.powerBp as number;

/**
 * Every skill as data (docs/PODERES.md §10, stage 2; how to read a program in `skill-cast.ts`). Each program
 * says, in the order it happens, what the skill does; `packages/sim/src/battle/cast.ts` runs it.
 */
export const SKILL_CASTS: Record<SkillId, SkillCast> = {
  // ——— Warrior ———
  shieldBash: {
    form: 'strike',
    type: 'physical',
    steps: [
      { k: 'enemy' },
      { k: 'face', to: 'e' },
      { k: 'special', fn: 'bashCharge', when: only('bashCharge') },
      { k: 'near', at: 'e', r: { fx: 'gateBlade', key: 'radius' }, pick: ['notE', 't', 'wall'], withE: true, when: { uq: 'gateBlade' } },
      { k: 'one', of: 'e', when: { notUq: 'gateBlade' } },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: { uq: 'gateBlade', then: { mul: [DMG, { fx: 'gateBlade', key: 'damageBp' }] }, else: DMG } },
          { k: 'stun', t: 'stun', heavy: true, when: { uq: 'gateBlade' } },
          { k: 'stun', t: 'stun', when: { notUq: 'gateBlade' } },
        ],
      },
      { k: 'boom', at: 'e', r: { uq: 'gateBlade', then: { fx: 'gateBlade', key: 'radius' }, else: 500 }, kind: 'bash' },
      // The gate blade's forms (docs/UNICAS.md §3): the wave, the rift, the gate shut.
      { k: 'special', fn: 'gateWave', when: { form: 'gateBlade', key: 'a' } },
      { k: 'special', fn: 'gateRift', when: { form: 'gateBlade', key: 'b' } },
      { k: 'special', fn: 'gateShut', when: { form: 'gateBlade', key: 'final' } },
      { k: 'near', at: 'e', r: FORKS.bashCleave.radius, pick: ['notE', 't'], nearest: FORKS.bashCleave.extra, into: 'cleave', when: only('bashCleave') },
      {
        k: 'each',
        of: 'cleave',
        when: only('bashCleave'),
        do: [
          { k: 'dmg', amt: { mul: [DMG, FORKS.bashCleave.damageBp] } },
          { k: 'stun', t: 'stun' },
          { k: 'boom', at: 'o', r: 400, kind: 'bash' },
        ],
      },
    ],
  },
  challenge: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'near', at: 'h', r: 'radius', pick: ['wall'] },
      { k: 'special', fn: 'challengeWalls', n: WARRIOR.challenge.wallRadius },
      { k: 'each', of: 'hits', do: [{ k: 'taunt', t: 'duration' }, { k: 'slow', bp: FORKS.warCry.slowBp, t: 'duration', when: only('warCry') }] },
      { k: 'taunter', t: 'duration', r: 'radius' },
      // The last watch's parapet (docs/UNICAS.md §3).
      { k: 'special', fn: 'parapet', when: { form: 'lastWatch', key: 'a' } },
      { k: 'record', of: 'hits' },
    ],
  },
  oath: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'near', at: 'h', r: 'radius', pick: ['wall'] },
      { k: 'each', of: 'hits', do: [{ k: 'taunt', t: 'duration' }] },
      { k: 'taunter', t: 'duration', r: 'radius' },
      { k: 'set', to: 'h', f: 'invuln', v: 'duration' },
      { k: 'highlight' },
      { k: 'allies', at: 'h', r: 'radius', notSelf: true, into: 'shared', when: only('sharedOath') },
      { k: 'each', of: 'shared', when: only('sharedOath'), do: [{ k: 'set', to: 'ho', f: 'invuln', v: { mul: ['duration', FORKS.sharedOath.durationBp] }, up: true }] },
      { k: 'special', fn: 'secondWind', n: FORKS.secondWind.healBp, when: only('secondWind') },
      { k: 'special', fn: 'oathRaise', when: { uq: 'posternMedal' } },
      { k: 'record', of: 'hits' },
    ],
  },
  lowBlow: {
    form: 'strike',
    type: 'physical',
    steps: [
      { k: 'enemy' },
      { k: 'face', to: 'e' },
      { k: 'dmg', on: 'e', amt: DMG },
      { k: 'mark', on: 'e', bp: byFork({ exposeBlow: FORKS.exposeBlow.markBp }, SKILL_FX.lowBlow.markBp), t: 'duration' },
      { k: 'slow', on: 'e', bp: FORKS.crippleBlow.slowBp, t: FORKS.crippleBlow.slowTicks, when: only('crippleBlow') },
      { k: 'boom', at: 'e', r: 500, kind: 'lowBlow' },
    ],
  },
  shieldSlam: {
    form: 'cone',
    type: 'physical',
    steps: [
      { k: 'aim' },
      { k: 'keep', name: 'r', v: fbp('radius', 'wideSlam', FORKS.wideSlam.radiusBp) },
      { k: 'cone', r: { v: 'r' }, deg: byFork({ wideSlam: FORKS.wideSlam.coneDeg }, SKILL_FX.shieldSlam.coneDeg) },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: fbp(DMG, 'hardSlam', FORKS.hardSlam.damageBp) },
          { k: 'stun', t: byFork({ hardSlam: FORKS.hardSlam.stun }, 'stun') },
          { k: 'shove', d: SKILL_FX.shieldSlam.push },
        ],
      },
      { k: 'face', to: 'aim' },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'shieldSlam' },
      { k: 'record', of: 'hits' },
    ],
  },
  grapple: {
    form: 'strike',
    type: 'physical',
    steps: [
      { k: 'enemy' },
      { k: 'near', at: 'e', r: FORKS.twinChain.radius, pick: ['notE', 't'], nearest: FORKS.twinChain.extra, withE: true, when: only('twinChain') },
      { k: 'one', of: 'e', when: { notFork: 'twinChain' } },
      { k: 'face', to: 'e' },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'beam', from: 'h', to: 'o', kind: 'chainHook' },
          { k: 'dmg', amt: fbp(DMG, 'heavyChain', FORKS.heavyChain.damageBp) },
          { k: 'stun', t: fbp('stun', 'heavyChain', FORKS.heavyChain.stunBp) },
          { k: 'special', fn: 'grapplePull', n: SKILL_FX.grapple.gap },
        ],
      },
      { k: 'record', of: 'hits' },
    ],
  },
  decoy: {
    form: 'summon',
    type: 'physical',
    steps: [
      { k: 'point', ground: true },
      { k: 'keep', name: 'hp', v: fbp({ pres: 'amount' }, 'twinDecoy', FORKS.twinDecoy.hpBp) },
      { k: 'clearBots', kind: 'decoy' },
      { k: 'spots', pattern: 'pair', gap: FORKS.twinDecoy.gap, when: only('twinDecoy') },
      {
        k: 'bot',
        at: 'spot',
        b: { kind: 'decoy', hp: { v: 'hp' }, maxHp: { v: 'hp' }, damage: 0, radius: 0, every: 0, cd: 0, expires: after('duration'), taunt: 'radius', aura: 0, auraBp: 0, facing: { facing: 'out' }, anim: 'idle', animT: 0, reflectBp: when('spikedDecoy', FORKS.spikedDecoy.reflectBp) },
      },
      { k: 'boom', at: 'p', r: 700, kind: 'decoy' },
      ...recordNear('p', 'radius'),
    ],
  },
  shieldWall: {
    form: 'party',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'r', v: fbp('radius', 'wideWall', FORKS.wideWall.radiusBp) },
      { k: 'allies', at: 'h', r: { v: 'r' }, notSelf: true, into: 'w' },
      { k: 'each', of: 'w', do: [{ k: 'ward', to: 'ho', bp: SKILL_FX.shieldWall.alliesBp, t: 'duration' }] },
      { k: 'ward', to: 'h', bp: byFork({ bulwarkWall: FORKS.bulwarkWall.selfBp }, SKILL_FX.shieldWall.selfBp), t: 'duration' },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'shieldWall' },
    ],
  },
  defyShout: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'r', v: fbp('radius', 'wideShout', FORKS.wideShout.radiusBp) },
      { k: 'near', at: 'h', r: { v: 'r' }, pick: ['t', 'wall'] },
      { k: 'each', of: 'hits', do: [{ k: 'stun', t: fbp('stun', 'longShout', FORKS.longShout.stunBp) }, { k: 'taunt', t: 'duration' }] },
      { k: 'taunter', t: 'duration', r: { v: 'r' } },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'defyShout' },
      { k: 'record', of: 'hits' },
    ],
  },
  guardBanner: {
    form: 'zone',
    type: 'physical',
    steps: [
      {
        k: 'zone',
        at: 'h',
        z: { kind: 'banner', radius: 'radius', ticks: 'duration', every: 'every', damage: 0, slowBp: 0, wardBp: SKILL_FX.guardBanner.takenBp, steady: true, heal: when('rallyBanner', { healBp: FORKS.rallyBanner.hpBp }) },
      },
      { k: 'boom', at: 'h', r: 'radius', kind: 'banner' },
      ...recordAllies('h', 'radius'),
    ],
  },
  shieldEcho: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'near', at: 'h', r: 'radius', pick: ['t'] },
      { k: 'each', of: 'hits', do: [{ k: 'dmg', amt: fbp(DMG, 'thunderEcho', FORKS.thunderEcho.damageBp) }, { k: 'stun', t: fbp('stun', 'longEcho', FORKS.longEcho.stunBp) }] },
      { k: 'boom', at: 'h', r: 'radius', kind: 'shieldEcho' },
      { k: 'record', of: 'hits' },
    ],
  },
  lastStand: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'ticks', v: fbp('duration', 'longStand', FORKS.longStand.durationBp) },
      { k: 'set', to: 'h', f: 'undying', v: { v: 'ticks' }, up: true },
      { k: 'allies', at: 'h', r: 'radius', notSelf: true, into: 'shared', when: only('sharedStand') },
      { k: 'each', of: 'shared', when: only('sharedStand'), do: [{ k: 'set', to: 'ho', f: 'undying', v: { mul: [{ v: 'ticks' }, FORKS.sharedStand.durationBp] }, up: true }] },
      { k: 'boom', at: 'h', r: byFork({ sharedStand: 'radius' }, 900), kind: 'lastStand' },
    ],
  },

  // ——— Artificer ———
  turret: { form: 'summon', type: 'physical', count: true, steps: [{ k: 'point', ground: true }, { k: 'special', fn: 'turret' }] },
  spikeMine: {
    form: 'zone',
    type: 'physical',
    count: true,
    steps: [
      { k: 'point', ground: true },
      { k: 'spots', pattern: 'ring3', gap: FORKS.clusterMine.gap, when: only('clusterMine') },
      { k: 'capZones', kind: 'mine', n: 'amount' },
      {
        k: 'zone',
        at: 'spot',
        z: {
          kind: 'mine',
          radius: byFork({ clusterMine: { div: [{ times: ['radius', 3] }, 4] } }, 'radius'),
          ticks: 'duration',
          every: 0,
          damage: fbp(DMG, 'clusterMine', FORKS.clusterMine.damageBp),
          slowBp: byFork({ tarMine: FORKS.tarMine.slowBp }, 'slowBp'),
          warn: SKILL_FX.spikeMine.arm,
          stun: byFork({ tarMine: FORKS.tarMine.slowTicks }, SKILL_FX.spikeMine.slowTicks),
        },
      },
      { k: 'boom', at: 'p', r: 500, kind: 'minePlanted' },
    ],
  },
  flamethrower: {
    form: 'cone',
    type: 'fire',
    steps: [
      { k: 'aim' },
      { k: 'keep', name: 'r', v: fbp('radius', 'longFlame', FORKS.longFlame.radiusBp) },
      { k: 'cone', r: { v: 'r' }, deg: SKILL_FX.flamethrower.coneDeg },
      { k: 'keep', name: 'napalm', v: byFork({ napalm: FORKS.napalm.burnBp }, 10000) },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: DMG, type: 'fire' },
          { k: 'skipDead' },
          { k: 'dot', dmg: { mul: [pow(SKILL_FX.flamethrower.burn), { v: 'napalm' }] }, type: 'fire', every: SKILL_FX.flamethrower.every, t: { mul: ['duration', { v: 'napalm' }] } },
        ],
      },
      { k: 'face', to: 'aim' },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'flamethrower' },
      { k: 'record', of: 'hits' },
    ],
  },
  tuneUp: {
    form: 'ally',
    type: 'physical',
    steps: [
      { k: 'point' },
      { k: 'keep', name: 'amount', v: fbp('amount', 'deepRepair', FORKS.deepRepair.amountBp) },
      { k: 'keep', name: 'count', v: byFork({ wideRepair: 1 + FORKS.wideRepair.extra }, 1) },
      { k: 'special', fn: 'tuneUp', n: FORKS.wideRepair.bp },
    ],
  },
  gearGrenade: {
    form: 'zone',
    type: 'physical',
    steps: [
      { k: 'point' },
      // Maelis's kit's pair and flasks (docs/UNICAS.md §3): where its grenades fall (one at the point without them).
      { k: 'special', fn: 'kitSpots', when: { anyForm: 'maelisKit' } },
      {
        k: 'zone',
        at: 'spot',
        z: {
          kind: 'grenade',
          radius: 'radius',
          ticks: 'duration',
          every: 0,
          damage: { form: 'maelisKit', key: 'b', then: { mul: [pow(fbp('damage', 'shrapnel', FORKS.shrapnel.damageBp)), KIT_PAIR] }, else: pow(fbp('damage', 'shrapnel', FORKS.shrapnel.damageBp)) },
          slowBp: byFork({ shrapnel: FORKS.shrapnel.slowBp }, 0),
          heal: { when: { uq: 'maelisKit' }, v: { form: 'maelisKit', key: 'b', then: { mul: [{ pres: { fx: 'maelisKit', key: 'heal' } }, KIT_PAIR] }, else: { pres: { fx: 'maelisKit', key: 'heal' } } } },
          fm: formMark('maelisKit'),
        },
      },
    ],
  },
  repairDrone: {
    form: 'summon',
    type: 'physical',
    steps: [
      { k: 'clearBots', kind: 'drone' },
      {
        k: 'bot',
        at: 'h',
        b: {
          kind: 'drone',
          hp: 1,
          maxHp: 1,
          damage: { struct: 'amount' },
          radius: 'radius',
          every: 'every',
          cd: 0,
          expires: after('duration'),
          taunt: 0,
          aura: 0,
          auraBp: 0,
          facing: { facing: 'h' },
          anim: 'walk',
          animT: 0,
          spark: when('welderDrone', rec({ radius: FORKS.welderDrone.radius, damage: pow(FORKS.welderDrone.damage) })),
          cover: when('armorDrone', rec({ radius: FORKS.armorDrone.radius, takenBp: FORKS.armorDrone.takenBp })),
        },
      },
      { k: 'boom', at: 'h', r: 600, kind: 'drone' },
    ],
  },
  ironBarricade: {
    form: 'wall',
    type: 'physical',
    steps: [
      { k: 'point' },
      { k: 'cells', at: 'p', n: { add: ['amount', byFork({ longBarricade: FORKS.longBarricade.extra }, 0)] } },
      {
        k: 'zone',
        at: 'p',
        z: { kind: 'barricade', radius: 0, ticks: 'duration', cells: { cells: true }, slowBp: 'slowBp', every: byFork({ spikedBarricade: FORKS.spikedBarricade.every }, 1), damage: byFork({ spikedBarricade: pow(FORKS.spikedBarricade.damage) }, 0) },
      },
      { k: 'boom', at: 'p', r: 900, kind: 'barricade' },
    ],
  },
  teslaCoil: {
    form: 'zone',
    type: 'lightning',
    steps: [
      { k: 'point', ground: true },
      { k: 'clearZones', kind: 'coil', by: 'pid' },
      {
        k: 'zone',
        at: 'p',
        z: { kind: 'coil', radius: 'radius', ticks: 'duration', every: byFork({ fastCoil: FORKS.fastCoil.every }, 'every'), damage: DMG, slowBp: 0, arcs: { add: ['amount', byFork({ bigCoil: FORKS.bigCoil.extra }, 0)] } },
      },
      { k: 'boom', at: 'p', r: 600, kind: 'coilPlanted' },
    ],
  },
  automaton: { form: 'summon', type: 'physical', steps: [{ k: 'point', ground: true }, { k: 'special', fn: 'automaton' }] },
  fieldMortar: {
    form: 'zone',
    type: 'fire',
    count: true,
    steps: [
      { k: 'point' },
      {
        k: 'zone',
        at: 'p',
        z: { kind: 'mortar', radius: 'radius', ticks: { times: ['amount', 'every'] }, every: 'every', damage: DMG, slowBp: 0, pulses: 0, stun: { when: { has: 'stun' }, v: 'stun' }, burns: when('incendiaryMortar', true) },
      },
      { k: 'boom', at: 'p', r: 'radius', kind: 'mortarAim' },
      ...recordNear('p', 'radius'),
    ],
  },
  fortifyAll: {
    form: 'zone',
    type: 'physical',
    steps: [
      { k: 'zone', at: 'h', z: { kind: 'fortify', radius: 'radius', ticks: fbp('duration', 'longFortify', FORKS.longFortify.durationBp), coverBp: SKILL_FX.fortifyAll.takenBp } },
      { k: 'special', fn: 'reinforce', n: FORKS.reinforce.hpBp, when: only('reinforce') },
      { k: 'boom', at: 'h', r: 'radius', kind: 'fortifyAll' },
    ],
  },
  clockHound: {
    form: 'summon',
    type: 'physical',
    steps: [
      { k: 'point', ground: true },
      { k: 'keep', name: 'count', v: byFork({ packHound: FORKS.packHound.count }, 1) },
      { k: 'keep', name: 'hp', v: { mul: [{ pres: 'amount' }, byFork({ ironHound: FORKS.ironHound.hpBp, packHound: FORKS.packHound.hpBp }, 10000)] } },
      { k: 'clearBots', kind: 'hound' },
      { k: 'spots', pattern: 'circle', gap: 600, n: { v: 'count' } },
      {
        k: 'bot',
        at: 'spot',
        b: { kind: 'hound', hp: { v: 'hp' }, maxHp: { v: 'hp' }, damage: DMG, radius: 'radius', every: 'every', cd: 0, expires: after('duration'), taunt: SKILL_FX.clockHound.taunt, aura: 0, auraBp: 0, facing: { facing: 'fromH' }, anim: 'idle', animT: 0 },
      },
      { k: 'boom', at: 'p', r: 900, kind: 'hound' },
      ...recordNear('p', 2500),
    ],
  },

  // ——— Mage ———
  fireball: {
    form: 'projectile',
    type: 'fire',
    steps: [
      { k: 'point' },
      { k: 'launch', from: 'h', to: 'p', pr: { kind: 'fireball', target: 0, speed: 1000, damage: DMG, splash: 'radius', ignoresArmor: true, pierce: false, enchant: null, stun: 0, hostile: false } },
      ...recordNear('p', 'radius', false),
      { k: 'special', fn: 'fallenStaff', when: { uq: 'fallenStaff' } },
      { k: 'zone', at: 'p', when: only('embers'), z: { kind: 'burn', radius: 'radius', ticks: FORKS.embers.ticks, every: FORKS.embers.every, damage: pow(FORKS.embers.damage), slowBp: 0 } },
    ],
  },
  chainLightning: {
    form: 'chain',
    type: 'lightning',
    count: true,
    steps: [
      { k: 'enemyNear' },
      { k: 'keep', name: 'jumps', v: byFork({ longArc: FORKS.longArc.jumps, overcharge: FORKS.overcharge.jumps }, 'amount') },
      { k: 'keep', name: 'fall', v: byFork({ longArc: FORKS.longArc.fallBp }, SKILL_FX.chainLightning.fallBp) },
      { k: 'special', fn: 'chain' },
      { k: 'near', at: 'q', r: FORKS.overcharge.radius, pick: ['t'], into: 'burst', when: only('overcharge') },
      { k: 'each', of: 'burst', when: only('overcharge'), do: [{ k: 'dmg', amt: pow(FORKS.overcharge.damage), type: 'lightning', noReact: true }] },
      { k: 'boom', at: 'q', r: FORKS.overcharge.radius, kind: 'overcharge', when: only('overcharge') },
      { k: 'record', of: 'chain' },
    ],
  },
  acidDart: {
    form: 'strike',
    type: 'acid',
    steps: [
      { k: 'enemy' },
      { k: 'keep', name: 'ticks', v: fbp('duration', 'corrosiveDart', FORKS.corrosiveDart.durationBp) },
      { k: 'near', at: 'e', r: FORKS.splashDart.radius, pick: ['notE', 't'], withE: true, when: only('splashDart') },
      { k: 'one', of: 'e', when: { notFork: 'splashDart' } },
      { k: 'each', of: 'hits', do: [{ k: 'dmg', amt: DMG, type: 'acid' }, { k: 'acid', t: { v: 'ticks' } }] },
      { k: 'face', to: 'e' },
      { k: 'beam', from: 'h', to: 'e', kind: 'acidDart' },
    ],
  },
  frostLance: {
    form: 'line',
    type: 'frost',
    steps: [
      { k: 'aim' },
      { k: 'end', from: 'aim', range: byFork({ longLance: FORKS.longLance.castRange }, 'castRange') },
      { k: 'line', to: 'end', w: 'radius' },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: fbp(DMG, 'bitingLance', FORKS.bitingLance.damageBp), type: 'frost' },
          { k: 'slow', bp: 'slowBp', t: 'duration', when: { has: 'slowBp' } },
        ],
      },
      { k: 'beam', from: 'h', to: 'end', kind: 'frostLance' },
      { k: 'face', to: 'end' },
      { k: 'record', of: 'hits' },
    ],
  },
  frostWall: {
    form: 'wall',
    type: 'frost',
    count: true,
    steps: [
      { k: 'point' },
      { k: 'cells', at: 'p', n: 'amount' },
      {
        k: 'zone',
        at: 'p',
        z: { kind: 'frost', radius: 0, cells: { cells: true }, ticks: 'duration', every: byFork({ bitingFrost: FORKS.bitingFrost.every }, 1), damage: byFork({ bitingFrost: pow(FORKS.bitingFrost.damage) }, 0), slowBp: 'slowBp' },
      },
    ],
  },
  frostOrb: {
    form: 'projectile',
    type: 'frost',
    steps: [
      { k: 'point' },
      { k: 'special', fn: 'orbPath', n: fbp(SKILL_FX.frostOrb.step, 'deepOrb', FORKS.deepOrb.stepBp) },
      {
        k: 'zone',
        at: 'h',
        z: {
          kind: 'orb',
          radius: 'radius',
          ticks: { add: [{ v: 'steps' }, 'duration'] },
          every: 'every',
          damage: DMG,
          slowBp: byFork({ deepOrb: FORKS.deepOrb.slowBp }, 'slowBp'),
          vx: { v: 'vx' },
          vy: { v: 'vy' },
          steps: { v: 'steps' },
          burst: when('orbBurst', rec({ radius: FORKS.orbBurst.radius, damage: pow(FORKS.orbBurst.damage), stun: FORKS.orbBurst.stun })),
        },
      },
      ...recordNear('p', { times: ['radius', 2] }),
    ],
  },
  shadowChains: {
    form: 'chain',
    type: 'shadow',
    steps: [
      { k: 'enemy' },
      { k: 'near', at: 'e', r: 'radius', pick: ['notE', 't'], nearest: { add: [{ sub: ['amount', 1] }, byFork({ widerChains: FORKS.widerChains.extra }, 0)] }, withE: true },
      { k: 'keep', name: 'share', v: byFork({ heavyChains: FORKS.heavyChains.shareBp }, SKILL_FX.shadowChains.shareBp) },
      { k: 'special', fn: 'links' },
      { k: 'boom', at: 'e', r: 600, kind: 'shadowChains' },
      { k: 'record', of: 'hits' },
    ],
  },
  blink: {
    form: 'leap',
    type: 'fire',
    steps: [
      { k: 'point', range: byFork({ farBlink: FORKS.farBlink.castRange }, 'castRange') },
      { k: 'land' },
      { k: 'near', at: 'h', r: 'radius', pick: ['t'] },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: DMG, type: { fork: { frostBlink: 'frost' }, else: 'fire' } },
          { k: 'slow', bp: FORKS.frostBlink.slowBp, t: FORKS.frostBlink.slowTicks, when: only('frostBlink') },
        ],
      },
      { k: 'boom', at: 'h', r: 'radius', kind: { fork: { frostBlink: 'blinkFrost' }, else: 'blink' } },
      { k: 'move', to: 'land' },
      { k: 'boom', at: 'land', r: 600, kind: 'blinkIn' },
      { k: 'record', of: 'hits' },
    ],
  },
  storm: {
    form: 'zone',
    type: 'lightning',
    steps: [
      { k: 'point' },
      { k: 'near', at: 'p', r: 'radius', pick: ['t'], when: only('thunderclap') },
      { k: 'each', of: 'hits', when: only('thunderclap'), do: [{ k: 'dmg', amt: DMG, armor: 'ignore', type: 'lightning' }] },
      { k: 'boom', at: 'p', r: 'radius', kind: 'storm', when: only('thunderclap') },
      { k: 'record', of: 'hits', when: only('thunderclap') },
      { k: 'stop', when: only('thunderclap') },
      {
        k: 'zone',
        at: 'p',
        z: {
          kind: 'storm',
          radius: 'radius',
          ticks: { mul: ['duration', { uqBp: 'durationBp' }] },
          every: 'every',
          damage: pow({ div: ['damage', { div: ['duration', 'every'] }] }),
          slowBp: byFork({ stillStorm: FORKS.stillStorm.slowBp }, 0),
          follow: { when: { uq: 'stormEye' }, v: { hid: true } },
          fm: formMark('stormEye'),
        },
      },
      // The eye's two storms (docs/UNICAS.md §3): one more stays where it was cast.
      {
        k: 'zone',
        at: 'p',
        when: { form: 'stormEye', key: 'final' },
        z: {
          kind: 'storm',
          radius: 'radius',
          ticks: { mul: ['duration', { uqBp: 'durationBp' }] },
          every: 'every',
          damage: pow({ div: ['damage', { div: ['duration', 'every'] }] }),
          slowBp: byFork({ stillStorm: FORKS.stillStorm.slowBp }, 0),
          fm: { formBits: 'stormEye' },
        },
      },
      ...recordNear('p', 'radius', false),
    ],
  },
  blizzard: {
    form: 'zone',
    type: 'frost',
    steps: [
      { k: 'point' },
      {
        k: 'zone',
        at: 'p',
        z: { kind: 'blizzard', radius: 'radius', ticks: 'duration', every: 'every', damage: DMG, slowBp: 'slowBp', pulses: 0, stunEvery: when('deepFreeze', FORKS.deepFreeze.pulses), stun: when('deepFreeze', FORKS.deepFreeze.stun) },
      },
      ...recordNear('p', 'radius'),
    ],
  },
  dreamRift: {
    form: 'zone',
    type: 'shadow',
    steps: [
      { k: 'point' },
      { k: 'zone', at: 'p', z: { kind: 'rift', radius: fbp('radius', 'wideRift', FORKS.wideRift.radiusBp), ticks: 'duration', damage: fbp(DMG, 'deepRift', FORKS.deepRift.damageBp) } },
      ...recordNear('p', 'radius'),
    ],
  },
  fireRain: {
    form: 'zone',
    type: 'fire',
    steps: [
      { k: 'point' },
      {
        k: 'zone',
        at: 'p',
        z: { kind: 'meteors', radius: 'radius', ticks: 'duration', every: byFork({ emberStorm: FORKS.emberStorm.every }, 'every'), damage: fbp(DMG, 'greatMeteors', FORKS.greatMeteors.damageBp), slowBp: 0, blast: fbp(SKILL_FX.fireRain.blast, 'greatMeteors', FORKS.greatMeteors.blastBp) },
      },
      ...recordNear('p', 'radius'),
    ],
  },

  // ——— Ranger ———
  multishot: {
    form: 'projectile',
    type: 'physical',
    count: true,
    steps: [
      { k: 'point' },
      { k: 'near', at: 'p', r: 'radius', pick: ['t'] },
      { k: 'need', of: 'hits' },
      { k: 'special', fn: 'volley' },
      { k: 'record', of: 'hits' },
    ],
  },
  poisonArrow: {
    form: 'projectile',
    type: 'poison',
    steps: [
      { k: 'enemyNear' },
      { k: 'keep', name: 'dmg', v: fbp(DMG, 'virulentArrow', FORKS.virulentArrow.damageBp) },
      { k: 'keep', name: 'ticks', v: fbp('duration', 'virulentArrow', FORKS.virulentArrow.durationBp) },
      { k: 'end', from: 'e', range: 'castRange' },
      { k: 'line', to: 'end', w: FORKS.piercingVenom.width, when: only('piercingVenom') },
      { k: 'one', of: 'e', when: { notFork: 'piercingVenom' } },
      { k: 'each', of: 'hits', do: [{ k: 'poison', dmg: { v: 'dmg' }, every: 'every', t: { v: 'ticks' } }] },
      { k: 'beam', from: 'h', to: 'end', kind: 'poisonArrow', when: only('piercingVenom') },
      { k: 'beam', from: 'h', to: 'e', kind: 'poisonArrow', when: { notFork: 'piercingVenom' } },
      { k: 'face', to: 'e' },
      { k: 'record', of: 'hits' },
    ],
  },
  disengage: {
    form: 'leap',
    type: 'physical',
    steps: [
      { k: 'special', fn: 'awayPoint', n: byFork({ farLeap: FORKS.farLeap.castRange }, 'castRange') },
      { k: 'land' },
      { k: 'zone', at: 'h', z: { kind: 'caltrops', radius: 'radius', ticks: 'duration', every: 'every', slowBp: 'slowBp', damage: fbp(DMG, 'sharpCaltrops', FORKS.sharpCaltrops.damageBp) } },
      { k: 'boom', at: 'h', r: 'radius', kind: 'caltrops' },
      { k: 'move', to: 'land' },
    ],
  },
  piercingShot: {
    form: 'line',
    type: 'physical',
    steps: [
      { k: 'aim' },
      { k: 'end', from: 'aim', range: 'castRange' },
      { k: 'line', to: 'end', w: 'radius' },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: fbp(DMG, 'heavyShot', FORKS.heavyShot.damageBp), armor: 'pierce' },
          { k: 'slow', bp: 'slowBp', t: 'duration', when: { has: 'slowBp' } },
          { k: 'stun', t: FORKS.pinShot.stun, when: only('pinShot') },
        ],
      },
      { k: 'beam', from: 'h', to: 'end', kind: 'piercingShot' },
      { k: 'face', to: 'end' },
      { k: 'record', of: 'hits' },
    ],
  },
  net: {
    form: 'projectile',
    type: 'physical',
    steps: [
      { k: 'enemyNear', reach: { add: ['castRange', 500] } },
      { k: 'near', at: 'e', r: FORKS.wideNet.radius, pick: ['notE', 't'], nearest: FORKS.wideNet.max - 1, withE: true, when: only('wideNet') },
      { k: 'one', of: 'e', when: { notFork: 'wideNet' } },
      {
        k: 'each',
        of: 'hits',
        do: [
          {
            k: 'launch',
            from: 'h',
            to: 'o',
            pr: { kind: 'arrow', target: { oid: true }, speed: HEROES.ranger.projectileSpeed, damage: 1, splash: 0, ignoresArmor: true, pierce: false, enchant: null, stun: 'stun', hardStun: { is: only('steelNet') }, hostile: false },
          },
        ],
      },
      // The wanderer's double net (docs/UNICAS.md §3), before the leap.
      { k: 'special', fn: 'doubleNet', n: UNIQUE_FORMS.wandererCloak.b.fx.range as number, when: { form: 'wandererCloak', key: 'b' } },
      { k: 'special', fn: 'leapBack', when: { uq: 'wandererCloak' } },
    ],
  },
  hawk: {
    form: 'summon',
    type: 'physical',
    steps: [
      { k: 'clearBots', kind: 'hawk' },
      {
        k: 'bot',
        at: 'h',
        b: {
          kind: 'hawk',
          hp: 1,
          maxHp: 1,
          damage: DMG,
          radius: 'radius',
          every: 'every',
          cd: 0,
          expires: after('duration'),
          taunt: 0,
          aura: 0,
          auraBp: 0,
          facing: { facing: 'h' },
          anim: 'walk',
          animT: 0,
          markBp: byFork({ hawkEye: FORKS.hawkEye.markBp }, SKILL_FX.hawk.markBp),
          bleed: when('talons', rec({ damage: pow(FORKS.talons.damage), every: FORKS.talons.every, ticks: FORKS.talons.ticks })),
          reveal: when('hawkEye', FORKS.hawkEye.reveal),
        },
      },
      { k: 'boom', at: 'h', r: 600, kind: 'hawk' },
    ],
  },
  signalFlare: {
    form: 'zone',
    type: 'fire',
    steps: [
      { k: 'point' },
      { k: 'keep', name: 'r', v: fbp('radius', 'brightFlare', FORKS.brightFlare.radiusBp) },
      { k: 'zone', at: 'p', z: { kind: 'flare', radius: { v: 'r' }, ticks: 'duration', reveal: true } },
      { k: 'near', at: 'p', r: { v: 'r' }, pick: ['t'], into: 'burn', when: only('burningFlare') },
      { k: 'each', of: 'burn', when: only('burningFlare'), do: [{ k: 'dmg', amt: pow(FORKS.burningFlare.damage), type: 'fire' }] },
      { k: 'boom', at: 'p', r: { v: 'r' }, kind: 'signalFlare' },
      ...recordNear('p', { v: 'r' }, false),
    ],
  },
  snare: {
    form: 'zone',
    type: 'physical',
    steps: [
      { k: 'point', ground: true },
      { k: 'spots', pattern: 'pair', gap: FORKS.twinSnare.gap, when: only('twinSnare') },
      { k: 'capZones', kind: 'snare', n: 'amount' },
      {
        k: 'zone',
        at: 'spot',
        z: { kind: 'snare', radius: 'radius', ticks: 'duration', every: 0, damage: DMG, slowBp: 0, stun: 'stun', bleed: when('barbedSnare', rec({ damage: pow(FORKS.barbedSnare.damage), every: FORKS.barbedSnare.every, ticks: FORKS.barbedSnare.ticks })) },
      },
      { k: 'boom', at: 'p', r: 500, kind: 'snareSet' },
    ],
  },
  arrowRain: {
    form: 'zone',
    type: 'physical',
    steps: [
      { k: 'point' },
      {
        k: 'zone',
        at: 'p',
        z: {
          kind: 'rain',
          radius: 'radius',
          ticks: { mul: ['duration', { uqBp: 'durationBp' }] },
          every: 'every',
          damage: pow({ uq: 'mossWhistle', then: { mul: ['damage', { fx: 'mossWhistle', key: 'damageBp' }] }, else: 'damage' }),
          slowBp: byFork({ pinningRain: FORKS.pinningRain.slowBp }, { uq: 'mossWhistle', then: { fx: 'mossWhistle', key: 'slowBp' }, else: 0 }),
          reveal: { when: { uq: 'mossWhistle' }, v: true },
          fm: formMark('mossWhistle'),
        },
      },
      ...recordNear('p', 'radius', false),
    ],
  },
  titanArrow: {
    form: 'line',
    type: 'physical',
    steps: [
      { k: 'aim' },
      { k: 'point', from: 'aim' },
      { k: 'end', from: 'p', range: 'castRange' },
      { k: 'line', to: 'end', w: 'radius' },
      { k: 'each', of: 'hits', do: [{ k: 'dmg', amt: DMG, armor: 'ignore' }, { k: 'stun', t: FORKS.pinningArrow.stun, when: only('pinningArrow') }] },
      { k: 'near', at: 'p', r: FORKS.burstingArrow.radius, pick: ['t'], into: 'burst', when: only('burstingArrow') },
      { k: 'each', of: 'burst', when: only('burstingArrow'), do: [{ k: 'dmg', amt: { mul: [DMG, FORKS.burstingArrow.damageBp] } }] },
      { k: 'boom', at: 'p', r: FORKS.burstingArrow.radius, kind: 'arrowBurst', when: only('burstingArrow') },
      { k: 'beam', from: 'h', to: 'end', kind: 'titanArrow' },
      { k: 'face', to: 'end' },
      { k: 'record', of: 'hits' },
    ],
  },
  hiddenShot: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'set', to: 'h', f: 'hidden', v: fbp('duration', 'longHidden', FORKS.longHidden.durationBp) },
      { k: 'set', to: 'h', f: 'hiddenBp', v: byFork({ deadlyHidden: FORKS.deadlyHidden.damageBp }, SKILL_FX.hiddenShot.damageBp) },
      { k: 'boom', at: 'h', r: 800, kind: 'hiddenShot' },
    ],
  },
  eagleEye: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'special', fn: 'eagleRange' },
      { k: 'set', to: 'h', f: 'eagle', v: fbp('duration', 'longEye', FORKS.longEye.durationBp) },
      { k: 'set', to: 'h', f: 'keen', v: { is: only('keenEye') } },
      { k: 'boom', at: 'h', r: 900, kind: 'eagleEye' },
    ],
  },

  // ——— Cleric ———
  mend: {
    form: 'ally',
    type: 'holy',
    end: { at: 'a', anim: 'skill0' },
    steps: [
      { k: 'ally' },
      { k: 'face', to: 'a' },
      { k: 'keep', name: 'amount', v: { heal: 'amount' } },
      { k: 'heal', to: 'a', amt: { v: 'amount' } },
      { k: 'inspire', to: 'a' },
      { k: 'boom', at: 'a', r: 500, kind: 'mend' },
      { k: 'special', fn: 'mendChain', when: only('mendChain') },
      { k: 'ward', to: 'a', bp: FORKS.mendWard.takenBp, t: FORKS.mendWard.ticks, when: only('mendWard') },
    ],
  },
  faithRay: {
    form: 'line',
    type: 'holy',
    steps: [
      { k: 'aim' },
      { k: 'end', from: 'aim', range: 'castRange' },
      { k: 'keep', name: 'w', v: fbp('radius', 'brightRay', FORKS.brightRay.widthBp) },
      { k: 'line', to: 'end', w: { v: 'w' } },
      { k: 'each', of: 'hits', do: [{ k: 'dmg', amt: DMG, type: 'holy' }] },
      { k: 'keep', name: 'heal', v: fbp({ heal: 'amount' }, 'mendingRay', FORKS.mendingRay.healBp) },
      { k: 'allyLine', to: 'end', w: { v: 'w' }, notSelf: true, into: 'mended' },
      { k: 'each', of: 'mended', do: [{ k: 'heal', to: 'ho', amt: { v: 'heal' } }] },
      { k: 'beam', from: 'h', to: 'end', kind: 'faithRay' },
      { k: 'face', to: 'end' },
      { k: 'record', of: 'hits' },
    ],
  },
  piercingLight: {
    form: 'strike',
    type: 'holy',
    steps: [
      { k: 'enemy' },
      { k: 'dmg', on: 'e', amt: fbp(DMG, 'brightLance', FORKS.brightLance.damageBp), type: 'holy' },
      { k: 'hurt', r: 'radius' },
      { k: 'heal', to: 'a', amt: fbp({ heal: 'amount' }, 'mercyLight', FORKS.mercyLight.healBp), when: { at: 'a' } },
      { k: 'beam', from: 'e', to: 'a', kind: 'faithRay', when: { at: 'a' } },
      { k: 'face', to: 'e' },
      { k: 'beam', from: 'h', to: 'e', kind: 'piercingLight' },
    ],
  },
  renew: {
    form: 'ally',
    type: 'holy',
    steps: [
      { k: 'ally', self: true },
      { k: 'keep', name: 'ticks', v: fbp('duration', 'longRenew', FORKS.longRenew.durationBp) },
      { k: 'allies', at: 'a', r: FORKS.sharedRenew.radius, into: 'who', when: only('sharedRenew') },
      { k: 'one', of: 'a', into: 'who', when: { notFork: 'sharedRenew' } },
      { k: 'each', of: 'who', do: [{ k: 'set', to: 'ho', f: 'renew', v: { v: 'ticks' } }, { k: 'set', to: 'ho', f: 'renewBp', v: { healBp: 'amount' } }] },
      { k: 'boom', at: 'a', r: 600, kind: 'renew' },
      { k: 'record', of: 'who' },
    ],
  },
  sanctuary: {
    form: 'zone',
    type: 'holy',
    steps: [
      { k: 'point' },
      { k: 'keep', name: 'ward', v: { min: [byFork({ bulwark: FORKS.bulwark.takenBp }, 10000), { uq: 'dwarfMail', then: { fx: 'dwarfMail', key: 'takenBp' }, else: 10000 }] } },
      {
        k: 'zone',
        at: 'p',
        z: {
          kind: 'sanctuary',
          radius: 'radius',
          ticks: 'duration',
          every: 'every',
          damage: byFork({ consecrate: pow(FORKS.consecrate.damage) }, 0),
          slowBp: 0,
          heal: { heal: 'amount' },
          wardBp: { ifLess: [{ v: 'ward' }, 10000], then: { v: 'ward' }, else: 0 },
          follow: { when: { uq: 'dwarfMail' }, v: { hid: true } },
          fm: formMark('dwarfMail'),
        },
      },
      // The dwarf mail's walking ground (docs/UNICAS.md §3): one more stays where it was cast.
      {
        k: 'zone',
        at: 'p',
        when: { form: 'dwarfMail', key: 'final' },
        z: {
          kind: 'sanctuary',
          radius: 'radius',
          ticks: 'duration',
          every: 'every',
          damage: byFork({ consecrate: pow(FORKS.consecrate.damage) }, 0),
          slowBp: 0,
          heal: { heal: 'amount' },
          wardBp: { ifLess: [{ v: 'ward' }, 10000], then: { v: 'ward' }, else: 0 },
          fm: { formBits: 'dwarfMail' },
        },
      },
      ...recordAllies('p', 'radius'),
    ],
  },
  guardTotem: {
    form: 'zone',
    type: 'holy',
    steps: [
      { k: 'point' },
      {
        k: 'zone',
        at: 'p',
        z: {
          kind: 'totem',
          radius: 'radius',
          ticks: 'duration',
          every: byFork({ mendingTotem: FORKS.mendingTotem.every }, FORKS.lightTotem.every),
          damage: 0,
          slowBp: 0,
          wardBp: SKILL_FX.guardTotem.takenBp,
          heal: when('mendingTotem', { healBp: FORKS.mendingTotem.hpBp }),
          strike: when('lightTotem', pow(FORKS.lightTotem.damage)),
        },
      },
      ...recordAllies('p', 'radius'),
    ],
  },
  cleanse: {
    form: 'party',
    type: 'holy',
    steps: [
      { k: 'keep', name: 'r', v: fbp('radius', 'wideCleanse', FORKS.wideCleanse.radiusBp) },
      { k: 'keep', name: 'bp', v: { healBp: 'amount' } },
      { k: 'allies', at: 'h', r: { v: 'r' }, into: 'who' },
      {
        k: 'each',
        of: 'who',
        do: [
          { k: 'set', to: 'ho', f: 'stun', v: 0 },
          { k: 'set', to: 'ho', f: 'fear', v: 0 },
          { k: 'set', to: 'ho', f: 'sleep', v: 0 },
          { k: 'set', to: 'ho', f: 'curse', v: 0 },
          { k: 'set', to: 'ho', f: 'poison', v: 0 },
          { k: 'heal', to: 'ho', amt: { maxHp: { v: 'bp' }, of: 'ho' } },
          { k: 'ward', to: 'ho', bp: FORKS.wardCleanse.takenBp, t: FORKS.wardCleanse.ticks, when: only('wardCleanse') },
        ],
      },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'cleanse' },
      { k: 'record', of: 'who' },
    ],
  },
  sharedPrayer: {
    form: 'party',
    type: 'holy',
    steps: [
      { k: 'keep', name: 'bp', v: { healBp: fbp('amount', 'deepPrayer', FORKS.deepPrayer.amountBp) } },
      { k: 'allies', at: 'h', r: 'radius', into: 'who' },
      {
        k: 'each',
        of: 'who',
        do: [
          { k: 'heal', to: 'ho', amt: { maxHp: { v: 'bp' }, of: 'ho' } },
          { k: 'ward', to: 'ho', bp: FORKS.wardPrayer.takenBp, t: FORKS.wardPrayer.ticks, when: only('wardPrayer') },
          { k: 'boom', at: 'ho', r: 500, kind: 'prayer' },
        ],
      },
      { k: 'record', of: 'who' },
    ],
  },
  dawn: {
    form: 'party',
    type: 'holy',
    steps: [
      { k: 'special', fn: 'dawn' },
      { k: 'near', at: 'h', r: 'radius', pick: ['t'], into: 'smite', when: only('dawnSmite') },
      { k: 'each', of: 'smite', when: only('dawnSmite'), do: [{ k: 'dmg', amt: pow(FORKS.dawnSmite.damage), armor: 'ignore', type: 'holy' }, { k: 'boom', at: 'o', r: 400, kind: 'smite' }] },
      { k: 'boom', at: 'h', r: 'radius', kind: 'dawn' },
      { k: 'record', n: { v: 'n' } },
    ],
  },
  dawnJudgment: {
    form: 'circle',
    type: 'holy',
    steps: [
      { k: 'point' },
      { k: 'near', at: 'p', r: 'radius', pick: ['t'] },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: { inked: { mul: [DMG, SKILL_FX.dawnJudgment.inkedBp] }, else: DMG }, type: 'holy' },
          { k: 'blind', t: FORKS.blindingDawn.ticks, bp: FORKS.blindingDawn.blindBp, when: [only('blindingDawn'), { alive: 'o' }] },
        ],
      },
      { k: 'keep', name: 'bp', v: { healBp: FORKS.mercifulDawn.hpBp }, when: only('mercifulDawn') },
      { k: 'allies', at: 'p', r: 'radius', into: 'mended', when: only('mercifulDawn') },
      { k: 'each', of: 'mended', when: only('mercifulDawn'), do: [{ k: 'heal', to: 'ho', amt: { maxHp: { v: 'bp' }, of: 'ho' } }] },
      { k: 'boom', at: 'p', r: 'radius', kind: 'dawnJudgment' },
      { k: 'record', of: 'hits' },
    ],
  },
  choir: {
    form: 'party',
    type: 'holy',
    steps: [
      { k: 'keep', name: 'ticks', v: fbp('duration', 'longChoir', FORKS.longChoir.durationBp) },
      { k: 'keep', name: 'bp', v: { healBp: byFork({ loudChoir: FORKS.loudChoir.amount }, 'amount') } },
      { k: 'allies', at: 'h', r: 'radius', into: 'who' },
      { k: 'each', of: 'who', do: [{ k: 'set', to: 'ho', f: 'choir', v: { v: 'ticks' } }, { k: 'set', to: 'ho', f: 'choirBp', v: { v: 'bp' } }] },
      { k: 'boom', at: 'h', r: 'radius', kind: 'choir' },
      { k: 'record', of: 'who' },
    ],
  },
  intercession: {
    form: 'ally',
    type: 'holy',
    steps: [
      { k: 'ally', self: true },
      { k: 'set', to: 'a', f: 'intercede', v: fbp('duration', 'longIntercession', FORKS.longIntercession.durationBp) },
      { k: 'set', to: 'a', f: 'intercedeBp', v: byFork({ fullIntercession: FORKS.fullIntercession.amount }, 'amount') },
      { k: 'boom', at: 'a', r: 700, kind: 'intercession' },
    ],
  },

  // ——— Barbarian ———
  whirl: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'special', fn: 'wallAxe', when: { uq: 'wallAxe' } },
      { k: 'special', fn: 'whirl' },
      { k: 'near', at: 'h', r: 'radius', pick: ['t'], into: 'rend', when: only('rend') },
      { k: 'each', of: 'rend', when: only('rend'), do: [{ k: 'dot', dmg: pow(FORKS.rend.damage), type: 'physical', every: FORKS.rend.every, t: FORKS.rend.ticks }] },
      { k: 'set', to: 'h', f: 'spin', v: FORKS.whirlwind.hits, when: only('whirlwind') },
      { k: 'record', n: { v: 'n' } },
    ],
  },
  throwingAxe: {
    form: 'projectile',
    type: 'physical',
    steps: [
      { k: 'enemyNear' },
      { k: 'face', to: 'e' },
      { k: 'dmg', on: 'e', amt: DMG, steal: true, times: 2 },
      { k: 'dot', on: 'e', dmg: pow(FORKS.bleedingAxe.damage), type: 'physical', every: FORKS.bleedingAxe.every, t: FORKS.bleedingAxe.ticks, when: only('bleedingAxe') },
      { k: 'near', at: 'e', r: FORKS.cleavingAxe.radius, pick: ['notE', 't'], nearest: FORKS.cleavingAxe.extra, into: 'cleave', when: only('cleavingAxe') },
      { k: 'each', of: 'cleave', when: only('cleavingAxe'), do: [{ k: 'dmg', amt: { mul: [DMG, FORKS.cleavingAxe.damageBp] } }] },
      { k: 'beam', from: 'h', to: 'e', kind: 'axe' },
    ],
  },
  kick: {
    form: 'strike',
    type: 'physical',
    steps: [
      { k: 'enemy' },
      { k: 'face', to: 'e' },
      { k: 'dmg', on: 'e', amt: fbp(DMG, 'bruteKick', FORKS.bruteKick.damageBp), steal: true },
      { k: 'stun', on: 'e', t: 'stun' },
      { k: 'shove', on: 'e', d: byFork({ farKick: FORKS.farKick.push }, SKILL_FX.kick.push) },
      { k: 'boom', at: 'e', r: 600, kind: 'kick' },
    ],
  },
  brutalBlow: {
    form: 'strike',
    type: 'physical',
    steps: [
      { k: 'enemy' },
      { k: 'keep', name: 'd', v: { mul: [DMG, { add: [10000, { mul: [SKILL_FX.brutalBlow.maxBonusBp, { hurtBp: true }] }] }] } },
      { k: 'keep', name: 'd', v: { mul: [{ v: 'd' }, FORKS.executeBlow.bonusBp] }, when: [only('executeBlow'), { lowHp: FORKS.executeBlow.below }] },
      { k: 'face', to: 'e' },
      { k: 'dmg', on: 'e', amt: { v: 'd' }, steal: true },
      { k: 'stun', on: 'e', t: FORKS.stunBlow.stun, when: only('stunBlow') },
      { k: 'boom', at: 'e', r: 500, kind: 'brutalBlow' },
    ],
  },
  leap: {
    form: 'leap',
    type: 'physical',
    steps: [
      { k: 'point' },
      { k: 'special', fn: 'fangChain', when: { anyForm: 'tailFang' } },
      { k: 'land' },
      { k: 'boom', at: 'h', r: 400, kind: 'leapFrom' },
      { k: 'move', to: 'land' },
      { k: 'near', at: 'land', r: 'radius', pick: ['t'] },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: DMG, steal: true, when: { notForm: 'tailFang', key: 'a' } },
          // The tail fang's crescendo (docs/UNICAS.md §3): each leap of a chain harder.
          { k: 'dmg', amt: { mul: [DMG, { v: 'cres' }] }, steal: true, when: { form: 'tailFang', key: 'a' } },
          { k: 'killed' },
          { k: 'heal', to: 'h', amt: { maxHp: { fx: 'tailFang', key: 'healBp' }, of: 'h' }, when: [{ uq: 'tailFang' }, { notForm: 'tailFang', key: 'b' }] },
          // Its hunger: a leap of a chain heals more.
          { k: 'heal', to: 'h', amt: { maxHp: { mul: [{ fx: 'tailFang', key: 'healBp' }, { v: 'hunger' }] }, of: 'h' }, when: { form: 'tailFang', key: 'b' } },
          { k: 'stun', t: 'stun' },
          { k: 'slow', bp: FORKS.quake.slowBp, t: FORKS.quake.slowTicks, when: only('quake') },
        ],
      },
      { k: 'boom', at: 'land', r: 'radius', kind: { fork: { quake: 'quake' }, else: 'leap' } },
      { k: 'record', of: 'hits' },
      { k: 'special', fn: 'leapAgain' },
    ],
  },
  charge: {
    form: 'leap',
    type: 'physical',
    steps: [
      { k: 'point', range: byFork({ longCharge: FORKS.longCharge.castRange }, 'castRange') },
      { k: 'land' },
      { k: 'line', to: 'land', w: 'radius' },
      { k: 'beam', from: 'h', to: 'land', kind: 'charge' },
      { k: 'each', of: 'hits', do: [{ k: 'dmg', amt: fbp(DMG, 'crushingCharge', FORKS.crushingCharge.damageBp), steal: true }, { k: 'stun', t: fbp('stun', 'crushingCharge', FORKS.crushingCharge.stunBp) }] },
      { k: 'face', to: 'land' },
      { k: 'move', to: 'land' },
      { k: 'boom', at: 'land', r: 'radius', kind: 'charge' },
      { k: 'record', of: 'hits' },
    ],
  },
  terrify: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'r', v: fbp('radius', 'wideTerrify', FORKS.wideTerrify.radiusBp) },
      { k: 'near', at: 'h', r: { v: 'r' }, pick: ['t'] },
      { k: 'each', of: 'hits', do: [{ k: 'stun', t: 'stun' }, { k: 'mark', bp: byFork({ deepTerrify: FORKS.deepTerrify.markBp }, SKILL_FX.terrify.markBp), t: 'duration' }] },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'terrify' },
      { k: 'record', of: 'hits' },
    ],
  },
  bloodletting: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'set', to: 'h', f: 'bleedOn', v: fbp('duration', 'longLetting', FORKS.longLetting.durationBp) },
      { k: 'set', to: 'h', f: 'thirstBp', v: FORKS.thirstyLetting.lifestealBp, when: only('thirstyLetting') },
      { k: 'set', to: 'h', f: 'thirstBp', v: null, when: { notFork: 'thirstyLetting' } },
      { k: 'boom', at: 'h', r: 800, kind: 'bloodletting' },
    ],
  },
  frenzy: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'set', to: 'h', f: 'frenzy', v: 'duration' },
      { k: 'set', to: 'h', f: 'stun', v: 0 },
      { k: 'boom', at: 'h', r: 900, kind: 'frenzy' },
      { k: 'highlight' },
    ],
  },
  clanRoar: {
    form: 'party',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'ticks', v: fbp('duration', 'longRoar', FORKS.longRoar.durationBp) },
      { k: 'allies', at: 'h', r: 'radius', into: 'roused' },
      { k: 'each', of: 'roused', do: [{ k: 'set', to: 'ho', f: 'roar', v: { v: 'ticks' }, up: true }, { k: 'ward', to: 'ho', bp: SKILL_FX.clanRoar.takenBp, t: { v: 'ticks' } }] },
      { k: 'near', at: 'h', r: FORKS.dreadRoar.radius, pick: ['t'], into: 'dread', when: only('dreadRoar') },
      { k: 'each', of: 'dread', when: only('dreadRoar'), do: [{ k: 'stun', t: FORKS.dreadRoar.stun }] },
      { k: 'boom', at: 'h', r: 'radius', kind: 'clanRoar' },
      { k: 'record', of: 'roused' },
    ],
  },
  duel: {
    form: 'strike',
    type: 'physical',
    steps: [
      { k: 'special', fn: 'duelPick' },
      { k: 'keep', name: 'ticks', v: fbp('duration', 'longDuel', FORKS.longDuel.durationBp) },
      { k: 'set', to: 'e', f: 'duelBy', v: { hid: true } },
      { k: 'set', to: 'e', f: 'duelTicks', v: { v: 'ticks' } },
      { k: 'set', to: 'e', f: 'duelLeechBp', v: FORKS.bloodDuel.lifestealBp, when: only('bloodDuel') },
      { k: 'set', to: 'e', f: 'duelLeechBp', v: null, when: { notFork: 'bloodDuel' } },
      { k: 'taunt', on: 'e', t: { v: 'ticks' } },
      { k: 'set', to: 'h', f: 'target', v: { eid: true } },
      { k: 'face', to: 'e' },
      { k: 'beam', from: 'h', to: 'e', kind: 'duel' },
      { k: 'boom', at: 'e', r: 900, kind: 'duel' },
    ],
  },
  ancestralFury: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'set', to: 'h', f: 'areaBlow', v: fbp('duration', 'longFury', FORKS.longFury.durationBp) },
      { k: 'set', to: 'h', f: 'areaR', v: fbp('radius', 'wideFury', FORKS.wideFury.radiusBp) },
      { k: 'set', to: 'h', f: 'areaBp', v: 'amount' },
      { k: 'set', to: 'h', f: 'areaHoly', v: false },
      { k: 'set', to: 'h', f: 'avatarWardBp', v: null },
      { k: 'boom', at: 'h', r: 1500, kind: 'ancestralFury' },
    ],
  },

  // ——— Paladin ———
  smite: {
    form: 'strike',
    type: 'holy',
    steps: [
      { k: 'enemy' },
      { k: 'face', to: 'e' },
      { k: 'smite', on: 'e', amt: DMG },
      { k: 'stun', on: 'e', t: { form: 'gasparHammer', key: 'b', then: { add: [{ fx: 'gasparHammer', key: 'stun' }, UNIQUE_FORMS.gasparHammer.b.fx.stun as number] }, else: { fx: 'gasparHammer', key: 'stun' } }, heavy: true, when: [{ uq: 'gasparHammer' }, { alive: 'e' }] },
      // Ser Gaspar's hammer's forms (docs/UNICAS.md §3): the toll, the heavy hammer, the guard.
      { k: 'special', fn: 'smiteToll', when: { form: 'gasparHammer', key: 'a' } },
      { k: 'special', fn: 'smiteHeavy', when: { form: 'gasparHammer', key: 'b' } },
      { k: 'special', fn: 'gasparGuard', when: { form: 'gasparHammer', key: 'final' } },
      { k: 'near', at: 'e', r: FORKS.judgment.radius, pick: ['notE', 't', 'corrupt'], nearest: FORKS.judgment.extra, into: 'judged', when: only('judgment') },
      { k: 'each', of: 'judged', when: only('judgment'), do: [{ k: 'smite', amt: { mul: [DMG, FORKS.judgment.damageBp] } }, { k: 'shot', from: 'e', to: 'o', kind: 'smite' }] },
      { k: 'keep', name: 'heal', v: { mul: [{ v: 'dealt' }, FORKS.healingLight.healBp] }, when: [only('healingLight'), { positive: 'dealt' }] },
      { k: 'allies', at: 'h', r: FORKS.healingLight.radius, into: 'healed', when: [only('healingLight'), { positive: 'dealt' }] },
      { k: 'each', of: 'healed', when: [only('healingLight'), { positive: 'dealt' }], do: [{ k: 'heal', to: 'ho', amt: { v: 'heal' } }] },
    ],
  },
  blindingLight: {
    form: 'cone',
    type: 'holy',
    steps: [
      { k: 'aim' },
      { k: 'keep', name: 'r', v: fbp('radius', 'wideLight', FORKS.wideLight.radiusBp) },
      { k: 'cone', r: { v: 'r' }, deg: byFork({ wideLight: FORKS.wideLight.coneDeg }, SKILL_FX.blindingLight.coneDeg) },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'blind', t: 'duration', bp: SKILL_FX.blindingLight.blindBp },
          { k: 'dmg', amt: pow({ add: ['damage', byFork({ searingLight: FORKS.searingLight.damage }, 0)] }), type: 'holy' },
        ],
      },
      { k: 'face', to: 'aim' },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: { fork: { wideLight: 'blindWide' }, else: 'blind' } },
      { k: 'record', of: 'hits' },
    ],
  },
  atone: {
    form: 'self',
    type: 'holy',
    steps: [
      { k: 'set', to: 'h', f: 'atone', v: 'duration' },
      { k: 'set', to: 'h', f: 'atoneBp', v: { healBp: byFork({ greaterAtone: FORKS.greaterAtone.amount }, 'amount') } },
      { k: 'set', to: 'h', f: 'atoneShared', v: { is: only('sharedAtone') } },
      { k: 'boom', at: 'h', r: 700, kind: 'atone' },
    ],
  },
  holySeal: {
    form: 'strike',
    type: 'holy',
    steps: [
      { k: 'enemy' },
      { k: 'set', to: 'e', f: 'seal', v: 'duration' },
      { k: 'set', to: 'e', f: 'sealBp', v: { healBp: byFork({ brightSeal: FORKS.brightSeal.healBp }, 'amount') } },
      { k: 'set', to: 'e', f: 'sealBurn', v: rec({ damage: pow(FORKS.burningSeal.damage), every: FORKS.burningSeal.every, pid: { pid: true } }), when: only('burningSeal') },
      { k: 'set', to: 'e', f: 'sealBurn', v: null, when: { notFork: 'burningSeal' } },
      { k: 'face', to: 'e' },
      { k: 'boom', at: 'e', r: 600, kind: 'holySeal' },
    ],
  },
  aegis: { form: 'ally', type: 'holy', end: { at: 'a', anim: 'skill1' }, steps: [{ k: 'special', fn: 'aegis' }] },
  spinHammer: {
    form: 'zone',
    type: 'holy',
    steps: [
      { k: 'keep', name: 'r', v: byFork({ wideHammer: { mul: ['radius', FORKS.wideHammer.radiusBp] } }, 'radius') },
      { k: 'clearZones', kind: 'hammer', by: 'follow' },
      {
        k: 'zone',
        at: 'h',
        z: { kind: 'hammer', radius: { v: 'r' }, ticks: 'duration', every: 'every', damage: DMG, slowBp: 0, follow: { hid: true }, pulses: 0, stunEvery: when('stunHammer', FORKS.stunHammer.pulses), stun: when('stunHammer', FORKS.stunHammer.stun) },
      },
      ...recordNear('h', { v: 'r' }),
    ],
  },
  blessWeapon: {
    form: 'ally',
    type: 'holy',
    steps: [
      { k: 'special', fn: 'blessPick' },
      { k: 'set', to: 'a', f: 'blessed', v: fbp('duration', 'longBlessing', FORKS.longBlessing.durationBp) },
      { k: 'set', to: 'a', f: 'blessBp', v: byFork({ brightBlessing: FORKS.brightBlessing.amount }, 'amount') },
      { k: 'boom', at: 'a', r: 700, kind: 'blessWeapon' },
    ],
  },
  intervene: {
    form: 'leap',
    type: 'holy',
    steps: [
      { k: 'ally', notSelf: true },
      { k: 'land', at: 'a', dx: 500 },
      { k: 'beam', from: 'h', to: 'land', kind: 'intervene' },
      { k: 'move', to: 'land' },
      { k: 'ward', to: 'a', bp: byFork({ steadfast: FORKS.steadfast.takenBp }, SKILL_FX.intervene.takenBp), t: 'duration' },
      { k: 'heal', to: 'a', amt: { maxHp: { healBp: FORKS.healingStep.hpBp }, of: 'a' }, when: only('healingStep') },
      { k: 'near', at: 'a', r: 'radius', pick: ['t', 'wall'], into: 'drawn' },
      { k: 'each', of: 'drawn', do: [{ k: 'taunt', t: 'duration' }] },
      { k: 'taunter', t: 'duration', r: 'radius' },
      { k: 'boom', at: 'a', r: 700, kind: 'intervene' },
    ],
  },
  hallow: {
    form: 'zone',
    type: 'holy',
    steps: [
      { k: 'point' },
      {
        k: 'zone',
        at: 'p',
        z: {
          kind: 'hallow',
          radius: 'radius',
          ticks: { mul: ['duration', { uqBp: 'durationBp' }] },
          every: 'every',
          damage: DMG,
          slowBp: byFork({ faithWeight: FORKS.faithWeight.slowBp }, 0),
          heal: { healBp: { powBp: 'amount' } },
          follow: { when: { uq: 'vigilCandle' }, v: { hid: true } },
          fm: formMark('vigilCandle'),
        },
      },
      ...recordNear('p', 'radius', false),
    ],
  },
  holyRampart: {
    form: 'zone',
    type: 'holy',
    steps: [
      { k: 'zone', at: 'h', z: { kind: 'rampart', radius: 'radius', ticks: 'duration', every: 12, damage: 0, slowBp: 0, coverBp: SKILL_FX.holyRampart.takenBp, wardBp: when('sacredRampart', FORKS.sacredRampart.takenBp) } },
      { k: 'boom', at: 'h', r: 'radius', kind: 'rampart' },
      { k: 'buildings', at: 'h', r: 'radius', into: 'covered' },
      { k: 'record', of: 'covered' },
    ],
  },
  sacrifice: {
    form: 'self',
    type: 'holy',
    steps: [
      { k: 'set', to: 'h', f: 'sacrifice', v: fbp('duration', 'longSacrifice', FORKS.longSacrifice.durationBp) },
      { k: 'set', to: 'h', f: 'sacrificeR', v: 'radius' },
      { k: 'set', to: 'h', f: 'sacrificeBp', v: 'amount' },
      { k: 'set', to: 'h', f: 'sacrificeTakenBp', v: byFork({ wardedSacrifice: FORKS.wardedSacrifice.takenBp }, 10000) },
      { k: 'boom', at: 'h', r: 'radius', kind: 'sacrifice' },
    ],
  },
  avatarOfLight: {
    form: 'self',
    type: 'holy',
    steps: [
      { k: 'set', to: 'h', f: 'areaBlow', v: 'duration' },
      { k: 'set', to: 'h', f: 'areaR', v: 'radius' },
      { k: 'set', to: 'h', f: 'areaBp', v: byFork({ radiantAvatar: FORKS.radiantAvatar.amount }, 'amount') },
      { k: 'set', to: 'h', f: 'areaHoly', v: true },
      { k: 'set', to: 'h', f: 'avatarWardBp', v: byFork({ guardianAvatar: FORKS.guardianAvatar.takenBp }, SKILL_FX.avatarOfLight.takenBp) },
      { k: 'boom', at: 'h', r: SKILL_FX.avatarOfLight.radius, kind: 'avatarOfLight' },
    ],
  },

  // ——— Druid ———
  entangle: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'point' },
      { k: 'near', at: 'p', r: 'radius', pick: ['t', 'wall'] },
      { k: 'keep', name: 'held', v: { uq: 'oldRootStaff', then: { form: 'oldRootStaff', key: 'b', then: { add: [{ mul: ['stun', { fx: 'oldRootStaff', key: 'holdBp' }] }, UNIQUE_FORMS.oldRootStaff.b.fx.ticks as number] }, else: { mul: ['stun', { fx: 'oldRootStaff', key: 'holdBp' }] } }, else: 'stun' } },
      { k: 'keep', name: 'pulses', v: { max: [1, { div: [{ v: 'held' }, FORKS.strangle.every] }] }, when: only('strangle') },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: DMG },
          { k: 'slow', bp: 10000, t: { v: 'held' } },
          { k: 'ground', t: { v: 'held' } },
          {
            k: 'dot',
            dmg: { div: [{ mul: [DMG, FORKS.strangle.damageBp - 10000] }, { v: 'pulses' }] },
            type: 'physical',
            every: FORKS.strangle.every,
            t: { times: [{ v: 'pulses' }, FORKS.strangle.every] },
            when: only('strangle'),
          },
        ],
      },
      { k: 'special', fn: 'rootSpread', when: [{ uq: 'oldRootStaff' }, { some: 'hits' }] },
      // The old root staff's forms (docs/UNICAS.md §3) on all it holds: the bitter sap, the deep root's elites, the old forest.
      { k: 'special', fn: 'rootsHeld', when: [{ anyForm: 'oldRootStaff' }, { some: 'hits' }] },
      { k: 'zone', at: 'p', when: only('spores'), z: { kind: 'spores', radius: 'radius', ticks: { add: [{ v: 'held' }, FORKS.spores.ticks] }, every: 1, damage: 0, slowBp: FORKS.spores.slowBp, warn: { v: 'held' } } },
      { k: 'boom', at: 'p', r: 'radius', kind: 'entangle' },
      { k: 'record', of: 'hits' },
    ],
  },
  wasps: {
    form: 'strike',
    type: 'poison',
    steps: [
      { k: 'enemyNear', reach: { add: ['castRange', 500] } },
      { k: 'near', at: 'e', r: FORKS.denseWasps.radius, pick: ['notE', 't'], nearest: FORKS.denseWasps.extra, withE: true, when: only('denseWasps') },
      { k: 'one', of: 'e', when: { notFork: 'denseWasps' } },
      { k: 'each', of: 'hits', do: [{ k: 'sting', dmg: DMG, every: 'every', t: 'duration', jump: only('jumpingWasps') }] },
      { k: 'boom', at: 'e', r: 500, kind: 'wasps' },
    ],
  },
  sporeCloud: {
    form: 'zone',
    type: 'poison',
    steps: [
      { k: 'point' },
      { k: 'zone', at: 'p', z: { kind: 'plague', radius: fbp('radius', 'thickSpores', FORKS.thickSpores.radiusBp), ticks: 'duration', every: 'every', slowBp: 'slowBp', damage: fbp(DMG, 'venomSpores', FORKS.venomSpores.damageBp) } },
      { k: 'boom', at: 'p', r: 'radius', kind: 'plague' },
    ],
  },
  briarLash: {
    form: 'cone',
    type: 'physical',
    steps: [
      { k: 'aim' },
      { k: 'keep', name: 'r', v: fbp('radius', 'longLash', FORKS.longLash.radiusBp) },
      { k: 'cone', r: { v: 'r' }, deg: SKILL_FX.briarLash.coneDeg },
      {
        k: 'each',
        of: 'hits',
        do: [
          { k: 'dmg', amt: DMG },
          { k: 'dot', dmg: fbp(pow(SKILL_FX.briarLash.bleed), 'deepThorns', FORKS.deepThorns.bleedBp), type: 'physical', every: SKILL_FX.briarLash.every, t: 'duration' },
          { k: 'stun', t: 'stun' },
          { k: 'special', fn: 'briarPull', n: SKILL_FX.briarLash.pull },
        ],
      },
      { k: 'face', to: 'aim' },
      { k: 'boom', at: 'h', r: { v: 'r' }, kind: 'briarLash' },
      { k: 'record', of: 'hits' },
    ],
  },
  bearForm: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'bear', v: { mul: ['duration', { uqBp: 'durationBp' }] } },
      { k: 'special', fn: 'bear' },
      { k: 'near', at: 'h', r: FORKS.direBear.taunt, pick: ['wall'], into: 'drawn', when: only('direBear') },
      { k: 'each', of: 'drawn', when: only('direBear'), do: [{ k: 'taunt', t: { v: 'bear' } }] },
      { k: 'taunter', t: { v: 'bear' }, r: FORKS.direBear.taunt, when: only('direBear') },
      { k: 'boom', at: 'h', r: 1200, kind: 'bear' },
    ],
  },
  deepRoot: {
    form: 'ally',
    type: 'physical',
    steps: [
      { k: 'ally', self: true },
      { k: 'allies', at: 'a', r: FORKS.spreadingRoots.radius, into: 'who', when: only('spreadingRoots') },
      { k: 'one', of: 'a', into: 'who', when: { notFork: 'spreadingRoots' } },
      {
        k: 'each',
        of: 'who',
        do: [
          { k: 'set', to: 'ho', f: 'rooted', v: 'duration' },
          { k: 'set', to: 'ho', f: 'rootBp', v: { healBp: 'amount' } },
          { k: 'set', to: 'ho', f: 'rootWardBp', v: FORKS.barkskin.takenBp, when: only('barkskin') },
          { k: 'set', to: 'ho', f: 'rootWardBp', v: null, when: { notFork: 'barkskin' } },
        ],
      },
      { k: 'boom', at: 'a', r: 600, kind: 'deepRoot' },
      { k: 'record', of: 'who' },
    ],
  },
  wolfForm: {
    form: 'self',
    type: 'physical',
    steps: [
      { k: 'keep', name: 'ticks', v: fbp('duration', 'longWolf', FORKS.longWolf.durationBp) },
      { k: 'special', fn: 'wolfSpeed', n: byFork({ swiftWolf: FORKS.swiftWolf.amount }, 'amount') },
      { k: 'set', to: 'h', f: 'wolf', v: { v: 'ticks' } },
      { k: 'set', to: 'h', f: 'bleedOn', v: { v: 'ticks' }, up: true },
      { k: 'boom', at: 'h', r: 800, kind: 'wolfForm' },
    ],
  },
  thornWall: {
    form: 'wall',
    type: 'physical',
    steps: [
      { k: 'point' },
      { k: 'cells', at: 'p', n: { add: ['amount', byFork({ longThorns: FORKS.longThorns.extra }, 0)] } },
      { k: 'zone', at: 'p', z: { kind: 'thorns', radius: 0, cells: { cells: true }, ticks: 'duration', every: 'every', damage: fbp(DMG, 'sharpThorns', FORKS.sharpThorns.damageBp), slowBp: 'slowBp' } },
    ],
  },
  bloom: {
    form: 'zone',
    type: 'physical',
    steps: [
      {
        k: 'zone',
        at: 'h',
        z: {
          kind: 'bloom',
          radius: 'radius',
          ticks: { mul: ['duration', { uqBp: 'durationBp' }] },
          every: 'every',
          damage: byFork({ briars: pow(FORKS.briars.damage) }, 0),
          slowBp: byFork({ briars: FORKS.briars.slowBp }, 0),
          heal: { healBp: { powBp: 'amount' } },
          follow: { when: { uq: 'maelisSeed' }, v: { hid: true } },
          fm: formMark('maelisSeed'),
        },
      },
      { k: 'boom', at: 'h', r: 'radius', kind: 'bloom' },
      ...recordAllies('h', 'radius'),
    ],
  },
  plague: {
    form: 'zone',
    type: 'poison',
    steps: [
      { k: 'clearZones', kind: 'plague', by: 'follow' },
      { k: 'zone', at: 'h', z: { kind: 'plague', radius: 'radius', ticks: 'duration', every: 'every', damage: DMG, slowBp: byFork({ witheringPlague: FORKS.witheringPlague.slowBp }, 0), follow: { hid: true } } },
      { k: 'boom', at: 'h', r: 'radius', kind: 'plague' },
      ...recordNear('h', 'radius'),
    ],
  },
  rootQuake: {
    form: 'circle',
    type: 'physical',
    steps: [
      { k: 'point' },
      { k: 'near', at: 'p', r: 'radius', pick: ['t', 'ground'] },
      { k: 'each', of: 'hits', do: [{ k: 'dmg', amt: fbp(DMG, 'shatterQuake', FORKS.shatterQuake.damageBp) }, { k: 'stun', t: 'stun' }] },
      { k: 'zone', at: 'p', z: { kind: 'quake', radius: 'radius', ticks: fbp('duration', 'longQuake', FORKS.longQuake.durationBp), slowBp: 'slowBp' } },
      { k: 'boom', at: 'p', r: 'radius', kind: 'rootQuake' },
      { k: 'record', of: 'hits' },
    ],
  },
  forestAwakening: {
    form: 'summon',
    type: 'physical',
    steps: [
      { k: 'point', ground: true },
      { k: 'keep', name: 'count', v: byFork({ ancientTreant: FORKS.ancientTreant.count, threeTreants: FORKS.threeTreants.count }, SKILL_FX.forestAwakening.count) },
      { k: 'keep', name: 'hp', v: { mul: [{ pres: 'amount' }, byFork({ ancientTreant: FORKS.ancientTreant.hpBp, threeTreants: FORKS.threeTreants.hpBp }, 10000)] } },
      { k: 'keep', name: 'dmg', v: fbp(DMG, 'ancientTreant', FORKS.ancientTreant.damageBp) },
      { k: 'clearBots', kind: 'treant' },
      { k: 'spots', pattern: 'circle', gap: 600, n: { v: 'count' } },
      {
        k: 'bot',
        at: 'spot',
        b: { kind: 'treant', hp: { v: 'hp' }, maxHp: { v: 'hp' }, damage: { v: 'dmg' }, radius: 'radius', every: 'every', cd: 0, expires: after('duration'), taunt: SKILL_FX.forestAwakening.taunt, aura: 0, auraBp: 0, facing: { facing: 'fromH' }, anim: 'idle', animT: 0 },
      },
      { k: 'boom', at: 'p', r: 900, kind: 'treant' },
      ...recordNear('p', 2500),
    ],
  },
};


/**
 * The variants that change their skill's numbers: a multiplier (bp), a few more, or a number of their own. Applied
 * after the rank (`rankedSkill`), each field on its own.
 */
export const VARIANT_PATCH: Partial<Record<ForkId, Partial<Record<SkillField, FieldPatch>>>> = {
  bashCharge: { castRange: { set: FORKS.bashCharge.castRange } },
  magnet: { radius: { bp: FORKS.magnet.radiusBp }, duration: { bp: FORKS.magnet.durationBp } },
  meteor: { damage: { bp: FORKS.meteor.damageBp }, radius: { bp: FORKS.meteor.radiusBp }, cooldown: { bp: FORKS.meteor.cooldownBp } },
  longWall: { amount: { add: FORKS.longWall.extra } },
  thunderclap: { damage: { bp: FORKS.thunderclap.damageBp } },
  volley: { amount: { add: FORKS.volley.extra }, damage: { bp: FORKS.volley.damageBp } },
  steelNet: { stun: { bp: FORKS.steelNet.stunBp } },
  wideRain: { radius: { bp: FORKS.wideRain.radiusBp } },
  quake: { radius: { bp: FORKS.quake.radiusBp } },
  holyGround: { radius: { bp: FORKS.holyGround.radiusBp }, duration: { bp: FORKS.holyGround.durationBp } },
  deepGrove: { radius: { bp: FORKS.deepGrove.radiusBp }, duration: { bp: FORKS.deepGrove.durationBp } },
  wideBanner: { radius: { bp: FORKS.wideBanner.radiusBp } },
  whiteout: { radius: { bp: FORKS.whiteout.radiusBp } },
  longRampart: { duration: { bp: FORKS.longRampart.durationBp } },
  heavyMortar: { amount: { set: 1 }, damage: { bp: FORKS.heavyMortar.damageBp }, radius: { bp: FORKS.heavyMortar.radiusBp }, stun: { set: FORKS.heavyMortar.stun } },
  virulentPlague: { damage: { bp: FORKS.virulentPlague.damageBp } },
};
